import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {InputText} from "primereact/inputtext";
import {Toast} from "primereact/toast";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {getDefaultCadastreScale} from "@reva/shared/land-register";
import {formatValue} from "@reva/shared/number-format";

import AppreciationTabInputText from "../../component/appreciation/AppreciationTabInputText";
import ErrorIndicator from "../../component/ErrorIndicator";
import LoadingIndicator from "../../component/LoadingIndicator";
import MapLayerUnavailableHint from "../../component/MapLayerUnavailableHint";
import OpenLayersMap from "../../component/OpenLayersMap";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {useAreaMeasurement} from "../../hooks/useAreaMeasurement";
import {useMapSettings} from "../../hooks/useMapSettings";
import {usePdfPreview} from "../../hooks/usePdfPreview";
import drawIcon from "../../images/draw_icon.png";
import drawIconWhite from "../../images/draw_icon_white.png";
import {selectAddressData} from "../../redux/addressSlice";
import {LocationPdfType} from "../../redux/pdfConstants";
import {selectDarkTheme} from "../../redux/themeSlice";
import {LAYER_ALKIS, selectCapabilities, selectError, selectLoading} from "../../redux/wmtsSlice";
import {createScaleView, LayerFactory} from "../../util/maps";
import {landRegisterAttributions} from "../address/LandRegisterCard";
import PersistenceFinalMessage from "../object/PersistenceFinalMessage";
import SnapshotMessage from "../object/SnapshotMessage";
import CadastralParcelCards from "./CadastralParcelCards";

/**
 *
 * @returns {React.JSX.Element}
 */
export function TabCadastreLocation() {
    const {t, language} = useRevaTranslation();
    const addressData = useSelector(selectAddressData);

    const capabilitiesLoading = useSelector(selectLoading);
    const capabilitiesError = useSelector(selectError);
    const capabilities = useSelector(selectCapabilities);
    const alkisCapabilities = capabilities?.[LAYER_ALKIS];
    const isDarkTheme = useSelector(selectDarkTheme);

    const {initialView, onMapMoveEnd} = useMapSettings("cadastre_map_settings");

    const [measuring, setMeasuring] = useState(false);
    const {measuredArea, cancelDrawing, removeLastPoint, finishDrawing, drawFunction, vectorLayer, interactionOptions} =
        useAreaMeasurement();

    const toast = useRef(null);
    const drawIconSrc = isDarkTheme ? drawIcon : drawIconWhite;

    // Einbahnstraße: einmal geöffnet, bleibt die Mess-Kachel offen -- ein manuelles
    // Schließen ist nicht vorgesehen, das erledigt bereits der Tab-Wechsel (unmountet
    // TabCadastreLocation und damit auch diesen State).
    const startMeasuring = useCallback(() => setMeasuring(true), []);

    const handleCopy = useCallback(() => {
        if (measuredArea === null || measuredArea === undefined) return;
        const formated = formatValue(measuredArea, language);
        navigator.clipboard.writeText(formated);
        toast.current?.show({
            severity: "info",
            summary: "Info",
            detail: t("measurement.copyToast", {value: formated}),
            life: 3000,
        });
    }, [measuredArea, language, t]);

    // Tastatur-Shortcuts nur aktiv, während gemessen wird -- wie im dialogbasierten
    // Mess-Tool (Objekt > Grundstück): ESC bricht die laufende Zeichnung ab bzw. löscht
    // eine bereits gemessene Fläche; BACKSPACE entfernt den letzten Punkt; ENTER schließt
    // das Polygon. Die Mess-Kachel selbst schließt ESC nicht -- siehe startMeasuring.
    useEffect(() => {
        if (!measuring) return;

        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                cancelDrawing();
            }
            if (e.key === "Backspace") {
                e.preventDefault();
                removeLastPoint();
            }
            if (e.key === "Enter") {
                finishDrawing();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [measuring, cancelDrawing, removeLastPoint, finishDrawing]);

    const createAlkisMapModel = () => {
        if (!addressData) return null;
        if (alkisCapabilities == null) return null;

        return {
            view: createScaleView(addressData, getDefaultCadastreScale(addressData.address?.state_code), initialView, {
                minZoom: 16,
                maxZoom: 33,
            }),
            layers: [
                LayerFactory.createGeoserverWmtsLayer(
                    LAYER_ALKIS,
                    undefined,
                    undefined,
                    "page_address_alkis",
                    undefined,
                    {
                        params: {
                            attributions:
                                "<a target='_blank' href='https://www.adv-online.de/AdV-Produkte/Liegenschaftskataster/'>© ALKIS</a>",
                        },
                        title: "Flurkarte",
                        type: "base",
                        ...alkisCapabilities,
                        background: "#ffffff",
                    },
                ),
                LayerFactory.createGeoserverWmsLayer("reva:brd_negativ", undefined),
            ],
            switcher: {
                enabled: false,
            },
            eventHandlers: {
                moveend_event: onMapMoveEnd,
            },
            scaleLine: {
                enabled: true,
            },
            interaction: measuring
                ? {
                      enabled: true,
                      options: interactionOptions,
                      drawFunction: drawFunction,
                      layer: vectorLayer,
                  }
                : {enabled: false},
        };
    };

    const mapModel = createAlkisMapModel();

    return (
        <div className="flex flex-col h-full">
            <Toast ref={toast} position="top-center" />
            <LoadingIndicator loading={capabilitiesLoading} />
            <ErrorIndicator errorObject={capabilitiesError} titleI18nKey="error.error" />
            {!capabilitiesLoading && !capabilitiesError && (
                <>
                    <PersistenceFinalMessage />
                    <SnapshotMessage />
                    <div className="MapSplit flex-1 min-h-0">
                        {/* Linke Spalte in Scroll-Containment kapseln (wie Klimarisiken-Tab), damit
                            lange Texte die Flex-Row NICHT in die Höhe strecken und die Karte immer
                            gleich hoch bleibt; der linke Bereich bekommt bei Bedarf einen Scrollbalken. */}
                        <div className="MapSplitLeft flex flex-col">
                            <div className="flex-1 relative">
                                <div className="absolute w-full h-full overflow-auto">
                                    <AppreciationTabInputText page="location" tab="cadastre" />
                                    <LandRegisterInfoCard addressData={addressData} alkisMapModel={mapModel} />
                                    <CadastralParcelCards address={addressData?.address} />
                                    {measuring ? (
                                        <Card className="LandRegisterCard MapNoteCard bg-surface-100 dark:bg-surface-700 mb-6 mt-0">
                                            <h3 className="mt-0 mb-2">{t("landregister.area")}</h3>
                                            <p className="mt-0 mb-2">{t("measurement.instructions")}</p>
                                            <div className="p-inputgroup">
                                                <InputText
                                                    id="cadastre-measured-area"
                                                    lang="de"
                                                    aria-label={t("landregister.area")}
                                                    value={
                                                        measuredArea !== null ? formatValue(measuredArea, language) : ""
                                                    }
                                                    disabled
                                                />
                                                <span className="p-inputgroup-addon">m²</span>
                                                <Button
                                                    icon="pi pi-copy"
                                                    className="p-button-outlined"
                                                    disabled={measuredArea === null}
                                                    onClick={handleCopy}
                                                    tooltip={t("measurement.copy")}
                                                    tooltipOptions={{position: "top"}}
                                                />
                                            </div>
                                            <div
                                                className="mt-3"
                                                style={{
                                                    fontSize: "0.9375rem",
                                                    lineHeight: 1.6,
                                                    whiteSpace: "pre-line",
                                                }}
                                            >
                                                {t("measurement.dialogKeyboardShortcuts")}
                                            </div>
                                        </Card>
                                    ) : (
                                        <Button
                                            className="w-full flex items-center justify-center gap-2"
                                            disabled={!mapModel}
                                            onClick={startMeasuring}
                                        >
                                            <img alt="draw on" src={drawIconSrc} style={{width: "1.2rem"}} />
                                            <span>{t("measurement.button")}</span>
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="MapSplitRight relative">
                            <MapLayerUnavailableHint layers={[LAYER_ALKIS]} />
                            <AlkisMap alkisMapModel={mapModel} addressData={addressData} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

/**
 * Display ALKIS Map only (no measurement).
 * @param {MapModel} alkisMapModel map model
 * @param {PersistenceAddressData|null} addressData selected address data
 */
function AlkisMap({alkisMapModel, addressData}) {
    const {language} = useRevaTranslation();
    const pdfPreview = usePdfPreview(LocationPdfType.CADASTRE);

    // Memoisieren, damit ein reiner pdfPreview-Hover (nonce-Änderung, nur diese
    // Kind-Komponente rendert dann neu) KEINE neue model-Referenz erzeugt. Sonst
    // liefe der Karten-Effekt in OpenLayersMap erneut und updateView würde die
    // aktuelle Nutzer-Ansicht auf die (im Elter eingefrorene) Default-View zurücksetzen.
    const mapModelWithMarker = useMemo(() => {
        if (!addressData?.address) return null;
        if (!alkisMapModel) return null;
        return {
            ...alkisMapModel,
            layers: [...(alkisMapModel?.layers || []), LayerFactory.createMarkerLayer(addressData.address, language)],
        };
    }, [alkisMapModel, addressData?.address, language]);

    return (
        <div className="OpenLayersMapBackdrop w-full h-full">
            <OpenLayersMap model={mapModelWithMarker} pdfPreview={pdfPreview} />
        </div>
    );
}

/**
 * @param {PersistenceAddressData|null} addressData selected address data
 * @param {MapModel} alkisMapModel map model
 * @return {React.JSX.Element}
 */
function LandRegisterInfoCard({addressData, alkisMapModel}) {
    const {t} = useTranslation();
    const currentYear = new Date().getFullYear();

    const attributions = landRegisterAttributions();

    const attribution = attributions[addressData?.address.state_code] || attributions[0];
    const service_label = attribution.service_label || attributions[0].service_label;
    const service_license = attribution?.service_license;
    const license_url = attribution.license_url;
    const license_Label = attribution.license_label;

    return (
        <Card className={"LandRegisterCard MapNoteCard bg-surface-100 dark:bg-surface-700 mb-6 mt-0"}>
            <h3 className="mt-0 mb-2">{t("landregister.source")}</h3>
            <p className="mt-0 mb-2">
                Amtliches Liegenschaftskatasterinformationssystem, {service_label} [{currentYear}]<br />
                {service_license}
            </p>
            <h3 className="mt-0 mb-2">{t("landregister.license")}</h3>
            <p className="mt-0 mb-0">
                {license_url ? (
                    <a target="_blank" rel="noreferrer" href={license_url}>
                        {license_Label}
                    </a>
                ) : (
                    t("landregister.licenseUnknown")
                )}
            </p>
        </Card>
    );
}

export default TabCadastreLocation;
